/**
 * Canonical profile-sports list (#2698).
 *
 * Single source of truth for the sports a USER can pick as their primary /
 * secondary activities on their profile. Every `id` here MUST be a member of the
 * backend `ALLOWED_SPORTS` enum (`backend/src/schemas/index.ts`) that
 * `UpdateProfileSchema` validates `PUT /profile` against. The two lists are kept
 * in lockstep by the drift tripwire in
 * `backend/tests/integration/sports-drift.test.ts` (asserts set equality in BOTH
 * directions against THIS module, imported directly — no hand-copied fixture).
 * Adding a sport here without adding it to ALLOWED_SPORTS (or vice-versa) fails
 * that test.
 *
 * Consumers (all read the SAME list, so the offered set can't diverge):
 *   - Onboarding Activities step: web `components/onboarding/steps/ActivitiesStep.tsx`
 *     + native sibling `mobile/.../steps/ActivitiesStep.tsx` (via `@shared/data/sports`).
 *     Renders `label` (short chip caption).
 *   - Profile settings: web `pages/settings/ProfileSettingsPage.tsx` + native
 *     `mobile/app/(protected)/settings/profile.tsx`. Renders `id` (full name).
 *
 * `label` is the short chip caption; where it differs from `id` it is only an
 * abbreviation (Strength Training -> "Strength", General Fitness -> "Fitness").
 * `id` is the wire value sent to the API.
 */
export interface ProfileSport {
  readonly id: string;
  readonly label: string;
}

export const PROFILE_SPORTS: readonly ProfileSport[] = [
  { id: 'Running', label: 'Running' },
  { id: 'Trail Running', label: 'Trail Running' },
  { id: 'Cycling', label: 'Cycling' },
  { id: 'Mountain Biking', label: 'Mountain Biking' },
  { id: 'Walking', label: 'Walking' },
  { id: 'Hiking', label: 'Hiking' },
  { id: 'Swimming', label: 'Swimming' },
  { id: 'Triathlon', label: 'Triathlon' },
  { id: 'Rowing', label: 'Rowing' },
  { id: 'Paddling', label: 'Paddling' },
  { id: 'Skiing', label: 'Skiing' },
  { id: 'Snowboarding', label: 'Snowboarding' },
  { id: 'Strength Training', label: 'Strength' },
  { id: 'Yoga', label: 'Yoga' },
  { id: 'Wheelchair', label: 'Wheelchair' },
  { id: 'General Fitness', label: 'Fitness' },
  { id: 'Other', label: 'Other' },
] as const;

/** Just the wire values, for schema-legality checks and mirror lists. */
export const PROFILE_SPORT_IDS: readonly string[] = PROFILE_SPORTS.map((s) => s.id);

/**
 * Selection caps — mirror `UpdateProfileSchema`: one `primary_sport` plus
 * `secondary_sports` with `.max(5)` (`backend/src/schemas/index.ts`). (#2698)
 */
export const MAX_SECONDARY_SPORTS = 5;
export const MAX_PROFILE_SPORTS = MAX_SECONDARY_SPORTS + 1; // 1 primary + 5 secondary
